// src/components/.../AdminDataErrorView.tsx
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Stack, useTheme, Box, Typography } from "@mui/material";
import { ThemeProvider } from "styled-components";
import { getAdminDataError, retryDataError } from "../../../api/adminDataError";
import { AdminDataErrorViewResponse } from "../../../types/adminDataErrorReponse";
import { RenderTable } from "../../common/splitview-table/RenderTable";
import { gridTheme } from "../../../utils/tableUtils";
import ButtonComponent from "../../common/button/Button";
import {
  setSelectedDropdownValue,
  setSelectedRow,
} from "../../../redux/actions/supportTicket";
import { useDispatch, useSelector } from "react-redux";

import {
  buildAdminErrorColumns,
  buildAdminErrorRows,
} from "./adminDataErrorTable";

import { useSnackbar } from "../../../components/SnakBar/AppSnackbarProvider";
import Download from "../../../components/common/DownloadGrid/Download";
import { setApiCall } from "../../../redux/actions/userSlice";
import { hasRolePermission } from "../../../constants/roleAccess";
import PageChangeWarningDialog from "../../common/PageChangeWarningDialog";
import BulkPasteFilter from "../../common/BulkFilter/BulkFilter";
import FilterManagerButton from "../../../components/common/FilterVariant/FilterManagerButton";

export const AdminDataErrorView = () => {
  const theme = useTheme();
  const [source, setSource] = useState<AdminDataErrorViewResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingReTry, setLoadingReTry] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isTriggerFilter, setIsTriggerFilter] = useState(false);
  const selectedRowIdsRef = useRef<Set<string>>(new Set());
  const dropdownValuesSetRef = useRef(false);
  const gridRef = useRef<any>(null);
  const prevPageRef = useRef<number | null>(null);
  const pendingPageRef = useRef<number | null>(null);
  const suppressPaginationRef = useRef(false);
  const freezeSelectionRef = useRef(false);
  const selectionSnapshotRef = useRef<Set<string>>(new Set());
  const [openPageChangeWarning, setOpenPageChangeWarning] = useState(false);
  const lastFetchedPlantKeyRef = useRef<string | null>(null);
  const [hasSelection, setHasSelection] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const currentRole = useSelector(
    (state: any) => state?.userRoles?.currentRole || null,
  );
  const isRoleReady = Boolean(currentRole);
  const canRetryDataError =
    isRoleReady && hasRolePermission(currentRole, "retry_data_error");
  const { enqueueSnackbar } = useSnackbar();
  const selectedPlant = useSelector((state: any) => state.userRoles.selectedPlant || []);

  const dispatch = useDispatch();
  const { isSelectionRemove } = useSelector(
    (state: any) => state.supportTicket,
  );

  // --- data fetch (unchanged) ---
  useEffect(() => {
     if (!selectedPlant || selectedPlant.length === 0) {
    setSource([])
    lastFetchedPlantKeyRef.current = null;
    return;
  }

    const plantKey = selectedPlant.join(",");
    if (lastFetchedPlantKeyRef.current === plantKey) {
      return;
    }

    lastFetchedPlantKeyRef.current = plantKey;
    fetchData();
    
  }, [selectedPlant]);
  const fetchData = async () => {
    setLoading(true);
    dispatch(setApiCall(true));
    try {
      const data = await getAdminDataError();
      setSource(data);
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
      dispatch(setApiCall(false));

    }
  };
  // --- columns & rows from helpers ---
  const columns = useMemo(() => buildAdminErrorColumns(), []);
  const rows = useMemo(
    () => buildAdminErrorRows(source, columns),
    [source, columns],
  );

  const restoreSelectionSnapshot = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    const snapshot = selectionSnapshotRef.current;
    if (!snapshot.size) return;

    api.forEachNode((node: any) => {
      const nodeId = String(node.id);
      node.setSelected(snapshot.has(nodeId));
    });

    setSelectedCount(snapshot.size);
    setHasSelection(snapshot.size > 0);
    const rows = api.getSelectedRows?.() ?? [];
    setSelectedRowData(rows);
    dispatch(setSelectedRow(rows));
  }, [dispatch]);

  const handleSelectionChanged = useCallback(
    (event: any) => {
      if (freezeSelectionRef.current) return;
      const nodes = event.api.getSelectedNodes();
      selectedRowIdsRef.current = new Set(nodes.map((n: any) => String(n.id)));
      dispatch(setSelectedRow(nodes.map((n: any) => n.data)));
      setSelectedRowData(nodes.map((n: any) => n.data));
      setHasSelection(nodes.length > 0);
      setSelectedCount(nodes.length);

      // Set default dropdown values only once when rows are first selected
      if (nodes.length > 0 && !dropdownValuesSetRef.current) {
        dropdownValuesSetRef.current = true;
        dispatch(
          setSelectedDropdownValue({
            menuId: "Data Error",
            categoryId: "Data",
            functionId: "Data",
          }),
        );
      }
      if (nodes.length === 0) {
        dropdownValuesSetRef.current = false;
      }
    },
    [dispatch],
  );

  const handlePageChange = useCallback(() => {
    if (suppressPaginationRef.current) return;
    const api = gridRef?.current?.api;
    if (!api) return;
    const current = api.paginationGetCurrentPage?.() ?? 0;
    const prev = prevPageRef.current;
    if (prev === null) {
      prevPageRef.current = current;
      return;
    }
    if (current !== prev) {
      if (selectedRowIdsRef.current.size > 0) {
        freezeSelectionRef.current = true;
        selectionSnapshotRef.current = new Set(selectedRowIdsRef.current);
        suppressPaginationRef.current = true;
        api.paginationGoToPage(prev);
        suppressPaginationRef.current = false;
        pendingPageRef.current = current;
        setOpenPageChangeWarning(true);
      } else {
        api.deselectAll();
        selectedRowIdsRef.current = new Set();
        dropdownValuesSetRef.current = false;
        setHasSelection(false);
        setSelectedCount(0);
        prevPageRef.current = current;
      }
    }
  }, [gridRef]);

  const deSelectItems = () => {
    const api = gridRef.current?.api;
    if (api) api.deselectAll();
    selectedRowIdsRef.current = new Set();
    setHasSelection(false);
    setSelectedCount(0);
  };

  const handlePageChangeConfirm = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    freezeSelectionRef.current = false;
    selectionSnapshotRef.current = new Set();
    api.deselectAll();
    selectedRowIdsRef.current = new Set();
    dropdownValuesSetRef.current = false;
    setHasSelection(false);
    setSelectedCount(0);
    setSelectedRowData([]);
    dispatch(setSelectedRow([]));

    if (pendingPageRef.current !== null) {
      suppressPaginationRef.current = true;
      api.paginationGoToPage(pendingPageRef.current);
      suppressPaginationRef.current = false;
      prevPageRef.current = pendingPageRef.current;
      pendingPageRef.current = null;
    }
    setOpenPageChangeWarning(false);
  }, [dispatch]);

  const handlePageChangeCancel = useCallback(() => {
    pendingPageRef.current = null;
    freezeSelectionRef.current = false;
    restoreSelectionSnapshot();
    setOpenPageChangeWarning(false);
  }, [restoreSelectionSnapshot]);

  useEffect(() => {
    if (openPageChangeWarning) return;
    if (!isSelectionRemove) return;
    deSelectItems();
  }, [isSelectionRemove, openPageChangeWarning]);
  const reTryHandler = async () => {
    if (!canRetryDataError) return;
    try {
      const errorIds = selectedRowData?.map((item) => item?.errorId) || [];
      setLoadingReTry(true);
      const response = await retryDataError(errorIds);
      if (
        response.toLowerCase().includes("success") ||
        response.toLowerCase().includes("ok")
      ) {
        enqueueSnackbar({
          message: "Submitted Successfully",
          severity: "success",
          autoHideDuration: 2500,
        });
        setLoadingReTry(false);
        deSelectItems();
      } else {
        enqueueSnackbar({
          message: `Failed to Submit,Please re-try`,
          severity: "error",
          autoHideDuration: 2500,
        });
        setLoadingReTry(false);
      }
    } catch (e) {
      enqueueSnackbar({
        message: `Failed to Submit,Please re-try`,
        severity: "error",
        autoHideDuration: 2500,
      });
      setLoadingReTry(false);
    } finally {
      setLoadingReTry(false);
    }
  };

  const handleResetFilters = useCallback(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    api.setFilterModel(null);
    if (typeof api.setGridOption === "function") {
      api.setGridOption("quickFilterText", "");
    }
    api.onFilterChanged();
    setIsTriggerFilter(true);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <Stack gap={2} padding={2} sx={{ padding: 2, paddingLeft: 0, flex: 1 }}>
        <Stack
          direction="row"
          gap={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
              Data Error View
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {(rows?.length ?? 0) > 0 && (
              <><ButtonComponent
                label="Reset Filters"
                className="take-action"
                  type="secondary"
                onClick={handleResetFilters}
                borderRadius="28px"
                width="auto"
                maxWidth="none"
              />  <FilterManagerButton    
                             gridRef={gridRef}    
                             tableName="Data Error View"    
                             userEmail={localStorage.getItem("userEmail") || ""}    
                             disabled={!rows?.length}  />
                               <BulkPasteFilter
                  gridRef={gridRef}
                  columns={columns}
                  setIsTriggerFilter={setIsTriggerFilter}
                  triggerClearFilter = {isTriggerFilter}
                />    </>
            )}
            <Download
              loading={loading}
              gridRef={gridRef}
              fileName="Data Error View"
              rowData={rows}
            />
            {canRetryDataError && (
              <ButtonComponent
                label={loadingReTry ? "Retrying..." : "Retry"}
                className="take-action"
                type="secondary"
                onClick={() => reTryHandler()}
                borderRadius="28px"
                disabled={!hasSelection || loadingReTry}
              />
            )}
          </Stack>
        </Stack>

        <RenderTable
          loading={loading}
          gridRef={gridRef}
          columns={columns}
          gridTheme={gridTheme}
          rows={rows}
          rowsPerPage={10}
          setGridReady={() => {}}
          sideBar={{}}
          minimized={null}
          emitEvent={() => {}}
          onChange={() => {}}
          setSelectedRow={() => {}}
          setOpenRejectPopup={() => {}}
          withSelectionColumn={true}
          selectionScope="page"
          onSelectionChanged={handleSelectionChanged}
          handleChangePage={handlePageChange}
          selectedCount={selectedCount}
        />

        <PageChangeWarningDialog
          open={openPageChangeWarning}
          selectedCount={selectedCount}
          onCancel={handlePageChangeCancel}
          onConfirm={handlePageChangeConfirm}
        />

        <Box paddingBottom={1}></Box>
      </Stack>
    </ThemeProvider>
  );
};
